import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../../../theme/colors';
import { SelectItem } from '../../../types';
import { Checkbox } from '../../Checkbox/Checkbox';
import { Divider } from '../../Divider/Divider';
import { SearchInput } from '../../../CoreComponents/base/SearchInput';
import { CheckboxWrapper, MultiselectOption, StyledOption } from './MultiselectOption';

interface Props {
  items: SelectItem[];
  isSearchable?: boolean;
  heading?: string;
  onItemSelect: (item: SelectItem) => void;
  selectedItems: SelectItem[];
  onClearFilters: () => void;
  footerContent?: ReactNode;
  selectAllNoneOptions?: { onSelectAllNone: () => void; allSelected: boolean; indeterminate: boolean };
}

const TableStyledOptions = styled('ul')`
  padding: 12px 8px;
  width: 100%;
  height: 300px;
  overflow-y: auto;
  border-top: 0;
  background: white;
  box-shadow:
    0px -4px 40px rgba(255, 255, 255, 0.1),
    0px 4px 24px rgba(16, 37, 62, 0.06),
    0px 24px 48px rgba(75, 82, 93, 0.04);
  list-style: none;
  border-radius: 0 0 4px 4px;
`;

const OptionsWrapper = styled('div')``;

const NoResultMessage = styled('div')`
  margin-left: 13px;
  padding-top: 6px;
`;

const Heading = styled('div')`
  margin-left: 3px;
  padding: 8px 0;
`;

export const TableMutliselect = ({
  items,
  onItemSelect,
  isSearchable = false,
  heading = '',
  selectedItems,
  selectAllNoneOptions,
  footerContent,
}: Props): ReactElement => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);

  useEffect(() => {
    if (isSearchable) {
      const updatedFilteredItems = items.filter((item) =>
        item.value.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredItems(updatedFilteredItems);
    }
  }, [items, searchValue, isSearchable]);

  return (
    <TableStyledOptions>
      {heading && (
        <Heading>
          <Typography variant='subtitle2' color={colors.primary[100]}>
            {heading}
          </Typography>
        </Heading>
      )}
      {isSearchable && (
        <SearchInput
          placeholder='Search'
          onChange={setSearchValue}
          sx={{ height: '36px', marginBottom: '0.5rem' }}
        />
      )}
      {footerContent}
      {selectAllNoneOptions && (
        <>
          <StyledOption
            onClick={selectAllNoneOptions.onSelectAllNone}
            role='option'
            isHighlighted={!!selectAllNoneOptions.allSelected}
          >
            <CheckboxWrapper>
              <Checkbox
                checked={!!selectAllNoneOptions.allSelected || !!selectAllNoneOptions.indeterminate}
                indeterminate={selectAllNoneOptions.indeterminate}
                readOnly
              />
            </CheckboxWrapper>
            <Typography variant='body2' color={colors.primary[100]}>
              {selectAllNoneOptions.allSelected ? 'Deselect All' : 'Select All'}
            </Typography>
          </StyledOption>
          <Divider />
          <br />
        </>
      )}
      <OptionsWrapper>
        {filteredItems.map((item) => (
          <MultiselectOption
            key={item.id}
            item={item}
            selectedItems={selectedItems}
            onSelect={onItemSelect}
          />
        ))}
        {!filteredItems.length && (
          <NoResultMessage>
            <Typography variant='body2' color={colors.neutral[50]}>
              No Results
            </Typography>
          </NoResultMessage>
        )}
      </OptionsWrapper>
    </TableStyledOptions>
  );
};
